import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-5958">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-5959">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-5960">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-5961">
            <span className="text-2xl" data-id="element-5962">📝</span>
            <div className="text-left" data-id="element-5963">
              <div className="text-sm font-bold text-indigo-900" data-id="element-5964">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-5965">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-5966">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-5967">
            <div className="text-center" data-id="element-5968">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-5969">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-5970">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-5971">→</div>
            <div className="text-center" data-id="element-5972">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-5973">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-5974">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-5975">
          <table className="w-full" data-id="element-5976">
            <thead data-id="element-5977">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-5978">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-5979">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-5980">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5981">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-5982">

                    <td className="px-4 py-3" data-id="element-5983">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-5984">

                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-5985">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-5986">

                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-5987">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-5988">
            <div className="flex items-start gap-3" data-id="element-5989">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-5990">
                <span className="text-blue-600 text-lg" data-id="element-5991">🔍</span>
              </div>
              <div data-id="element-5992">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-5993">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5994">
                  De <strong data-id="element-5995">stam</strong> krijg je door <strong data-id="element-5996">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-5997">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-5998">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-5999">
              <div className="flex items-start gap-3" data-id="element-6000">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-6001">
                  <span className="text-emerald-600 text-lg" data-id="element-6002">📋</span>
                </div>
                <div data-id="element-6003">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-6004">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6005">
                    <p data-id="element-6006">
                      <strong data-id="element-6007">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6008">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-6009">
                      <strong data-id="element-6010">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6011">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6012">
                      <strong data-id="element-6013">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6014">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-6015">
                      <strong data-id="element-6016">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-6017">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-6018">
            <div className="flex items-start gap-3" data-id="element-6019">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-6020">
                <span className="text-amber-600 text-lg" data-id="element-6021">💡</span>
              </div>
              <div data-id="element-6022">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-6023">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6024">
                  Bij <strong data-id="element-6025">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-6026">jij loopt</em> wordt{' '}
                  <em data-id="element-6027">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}